/**
 * Bridge Club Store Detail
 *
 * Detailed analytics for a single retail partner store showing:
 * - Store performance metrics
 * - Laura AI analysis
 * - Member list with segments
 * - Product performance
 * - Revenue trends
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import api from '../../services/api';
import LauraInsightsPanel from '../../components/Admin/LauraInsightsPanel';
import '../../components/Admin/LauraInsightsPanel.css';
import './BridgeClubPages.css';
import './BridgeClubStoreDetail.css';

interface Store {
  id: number;
  name: string;
  city: string;
  state: string;
  store_code: string;
  bridge_club_enabled: boolean;
}

interface Metrics {
  members: {
    total: number;
    active: number;
    active_rate: number;
    new_this_month: number;
  };
  revenue: {
    total: number;
    this_month: number;
    avg_order: number;
  };
  purchases: {
    total: number;
    this_month: number;
  };
  segments: {
    champion: number;
    loyal: number;
    at_risk: number;
    dormant: number;
  };
}

interface Member {
  id: number;
  name: string;
  email: string;
  joined: string;
  total_spent: number;
  purchases: number;
  last_purchase: string | null;
  punches: number;
}

interface Product {
  name: string;
  quantity: number;
  revenue: number;
}

interface LauraAnalysis {
  performance_summary: string;
  performance_rating: 'excellent' | 'good' | 'average' | 'needs_attention';
  member_insights: Array<{ insight: string; type: string; action: string }>;
  growth_opportunities: Array<{ opportunity: string; effort: string; impact: string }>;
  ai_generated: boolean;
}

interface StoreData {
  store: Store;
  metrics: Metrics;
  top_products: Product[];
  members: Member[];
  trends: {
    revenue: Array<{ month: string; revenue: number; purchases: number }>;
  };
  laura_analysis: LauraAnalysis;
}

const BridgeClubStoreDetail: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useParams<{ storeId: string }>();
  const [data, setData] = useState<StoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<StoreData>(`/admin/bridge-club/stores/${storeId}/detail/`);
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load store detail:', err);
      setError(err.response?.data?.error || 'Failed to load store details');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading store analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bridge-club-page">
        <div className="page-header">
          <div className="header-content">
            <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
              &larr; Back to Network
            </button>
            <h1>Store Details</h1>
          </div>
        </div>
        <div className="error-banner">
          {error || 'Failed to load data'}
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const { store, metrics, top_products, members, trends, laura_analysis } = data;

  return (
    <div className="bridge-club-page store-detail">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
            &larr; Back to Network
          </button>
          <h1>{store.name}</h1>
          <p>{store.city}, {store.state}</p>
        </div>
        <div className="store-status">
          <span className={`status-badge ${store.bridge_club_enabled ? 'active' : 'inactive'}`}>
            {store.bridge_club_enabled ? 'Active' : 'Inactive'}
          </span>
          {store.store_code && (
            <span className="store-code">Code: {store.store_code}</span>
          )}
        </div>
      </div>

      {/* Laura AI Analysis Panel */}
      <div className="laura-section">
        <LauraInsightsPanel
          analysis={laura_analysis}
          loading={false}
          title="Laura's Store Analysis"
        />
      </div>

      {/* Key Metrics */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{metrics.members.total}</div>
          <div className="stat-label">Total Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{metrics.members.active}</div>
          <div className="stat-label">Active Members</div>
          <div className="stat-sub">{metrics.members.active_rate}% active rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(metrics.revenue.total)}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(metrics.revenue.avg_order)}</div>
          <div className="stat-label">Avg Order Value</div>
        </div>
      </div>

      {/* Member Segments */}
      <div className="segments-row">
        <h3>Member Segments</h3>
        <div className="segments-grid">
          <div className="segment-card champion">
            <div className="segment-value">{metrics.segments.champion}</div>
            <div className="segment-label">Champions</div>
            <div className="segment-desc">$500+ / 90 days</div>
          </div>
          <div className="segment-card loyal">
            <div className="segment-value">{metrics.segments.loyal}</div>
            <div className="segment-label">Loyal</div>
            <div className="segment-desc">Active last 90 days</div>
          </div>
          <div className="segment-card at-risk">
            <div className="segment-value">{metrics.segments.at_risk}</div>
            <div className="segment-label">At Risk</div>
            <div className="segment-desc">90-180 days ago</div>
          </div>
          <div className="segment-card dormant">
            <div className="segment-value">{metrics.segments.dormant}</div>
            <div className="segment-label">Dormant</div>
            <div className="segment-desc">180+ days</div>
          </div>
        </div>
      </div>

      {/* Charts and Products */}
      <div className="detail-grid">
        {/* Revenue Trend */}
        <div className="chart-card">
          <h3>Revenue Trend</h3>
          {trends.revenue.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trends.revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8B0000"
                  strokeWidth={2}
                  dot={{ fill: '#8B0000' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Products */}
        <div className="chart-card">
          <h3>Top Products</h3>
          {top_products.length === 0 ? (
            <div className="no-data">No products sold</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={top_products.slice(0, 5)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  width={100}
                  tickFormatter={(v) => v.substring(0, 15) + (v.length > 15 ? '...' : '')}
                />
                <Tooltip />
                <Bar dataKey="quantity" fill="#1a472a" name="Units Sold" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Member List */}
      <div className="members-section">
        <div className="section-header">
          <h3>Top Members</h3>
          <span className="member-count">{members.length} members</span>
        </div>
        <div className="members-table">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Joined</th>
                <th>Purchases</th>
                <th>Total Spent</th>
                <th>Last Purchase</th>
                <th>Punches</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-row">No members found</td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr
                    key={member.id}
                    onClick={() => navigate(`/admin/bridge-club/members/${member.id}`)}
                    className="clickable"
                  >
                    <td>
                      <div className="member-info">
                        <span className="member-name">{member.name}</span>
                        <span className="member-email">{member.email}</span>
                      </div>
                    </td>
                    <td>{formatDate(member.joined)}</td>
                    <td className="center">{member.purchases}</td>
                    <td className="currency">{formatCurrency(member.total_spent)}</td>
                    <td>{formatDate(member.last_purchase)}</td>
                    <td>
                      <span className="punch-badge">{member.punches}/10</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubStoreDetail;
